import React from "react";
import { Box, Button, Card, Grid, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@emotion/react";
import BoltIcon from "@mui/icons-material/Bolt";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Ball, { ballColor } from "./Ball";
import { elevatedCard } from "./cardStyle";

const NumberPicker = ({
  maxNumber,
  ballCount = 3,
  ticketPriceLabel,
  selected,
  onToggleNumber,
  onQuickPick,
  onClear,
  onBuy,
  countdownEnded,
  insufficientBalance,
}) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Grid item xs={12} md={7}>
      <Card
        sx={{
          background: theme.palette.custom.dark_d,
          border: `1px solid ${theme.palette.custom.lgray}`,
          borderRadius: "16px",
          p: { xs: 2.5, md: 3 },
          ...elevatedCard,
        }}
      >
        {insufficientBalance && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              background: "#ff9c0022",
              border: `1px solid ${theme.palette.custom.yellow}66`,
              borderRadius: "10px",
              px: 1.6,
              py: 1,
              mb: 2,
            }}
          >
            <WarningAmberIcon sx={{ fontSize: 18, color: theme.palette.custom.yellow }} />
            <Typography sx={{ color: theme.palette.custom.white_gray, fontSize: "13px", fontWeight: 600 }}>
              Low balance — deposit using Buy Coins to purchase a ticket.
            </Typography>
          </Box>
        )}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography sx={{ color: theme.palette.custom.white_gray, fontWeight: 800, fontSize: "17px" }}>
            Pick your {ballCount} lucky numbers
          </Typography>
          <Button
            size="small"
            onClick={onQuickPick}
            startIcon={<BoltIcon />}
            sx={{
              color: theme.palette.custom.yellow,
              border: `1px solid ${theme.palette.custom.yellow}55`,
              borderRadius: "20px",
              px: 1.5,
              textTransform: "none",
              fontWeight: 700,
            }}
          >
            Quick Pick
          </Button>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(6, 1fr)", md: "repeat(10, 1fr)" },
            gap: { xs: 1.2, md: 1.5 },
            mt: 2,
          }}
        >
          {Array.from({ length: maxNumber || 0 }, (_, i) => i + 1).map((num) => {
            const isSel = selected?.includes(num);
            const c = ballColor(num, maxNumber);
            return (
              <Box
                key={num}
                onClick={() => onToggleNumber(num)}
                sx={{
                  aspectRatio: "1",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  userSelect: "none",
                  fontWeight: 700,
                  fontSize: { xs: "14px", md: "15px" },
                  color: isSel ? "#fff" : theme.palette.custom.white_gray,
                  background: isSel
                    ? `radial-gradient(circle at 30% 25%, #ffffff66 8%, ${c.base} 40%, ${c.dark} 100%)`
                    : "#ffffff10",
                  border: isSel ? "2px solid #ffffffaa" : "1px solid #ffffff22",
                  boxShadow: isSel
                    ? "0 4px 12px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.15)"
                    : "inset 0 1px 0 rgba(255,255,255,0.04), 0 2px 4px rgba(0,0,0,0.2)",
                  transition: "all .15s",
                  "&:hover": {
                    transform: "scale(1.08)",
                    border: `1px solid ${c.base}`,
                  },
                }}
              >
                {num}
              </Box>
            );
          })}
        </Box>

        <Box
          sx={{
            mt: 2.5,
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", gap: 1.2, alignItems: "center", flexWrap: "wrap" }}>
            {Array.from({ length: ballCount }, (_, i) => i).map((i) =>
              selected?.[i] ? (
                <Ball key={i} num={selected[i]} max={maxNumber} size={isXs ? 40 : 48} />
              ) : (
                <Box
                  key={i}
                  sx={{
                    width: isXs ? 40 : 48,
                    height: isXs ? 40 : 48,
                    borderRadius: "50%",
                    border: "2px dashed #ffffff44",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#ffffff55",
                    fontWeight: 700,
                  }}
                >
                  ?
                </Box>
              )
            )}
            {selected?.length > 0 && (
              <Button
                size="small"
                onClick={onClear}
                sx={{ color: theme.palette.custom.red, textTransform: "none", fontWeight: 700 }}
              >
                Clear
              </Button>
            )}
          </Box>

          <Button
            onClick={onBuy}
            disabled={countdownEnded}
            sx={{
              background: insufficientBalance ? theme.palette.custom.yellow : theme.palette.custom.green_green,
              color: insufficientBalance ? "#191919" : "#fff",
              fontWeight: 800,
              borderRadius: "24px",
              px: { xs: 3, md: 5 },
              py: 1.2,
              fontSize: { xs: "14px", md: "16px" },
              textTransform: "none",
              boxShadow: "0 8px 20px rgba(3,179,73,0.4), inset 0 1px 0 rgba(255,255,255,0.2)",
              "&:hover": {
                opacity: 0.9,
                background: insufficientBalance ? theme.palette.custom.yellow : theme.palette.custom.green_green,
              },
              "&.Mui-disabled": { background: "#ffffff22", color: "#ffffff66", boxShadow: "none" },
            }}
          >
            {insufficientBalance
              ? "Deposit"
              : ticketPriceLabel
              ? `Buy Ticket - ${ticketPriceLabel}`
              : "Buy Ticket"}
          </Button>
        </Box>
      </Card>
    </Grid>
  );
};

export default NumberPicker;
